import { useCallback, useEffect, useRef, useState } from "react";
import {
  ENERGIZED_MS,
  MEASURE,
  SETTLE_MS,
  SIP_MS,
  addMeasure,
  advanceFill,
  energyForCups,
  reactionForCups,
} from "./machine";
import type { CoffeePhase } from "./types";

const BUSY: CoffeePhase[] = ["settling", "sipping", "energized"];

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useCoffeeMachine() {
  const [phase, setPhase] = useState<CoffeePhase>("idle");
  const [isPourActive, setIsPourActive] = useState(false);
  const [cupFill, setCupFill] = useState(0);
  const [designerEnergy, setDesignerEnergy] = useState(5);
  const [cupsConsumed, setCupsConsumed] = useState(0);
  const [announcement, setAnnouncement] = useState("");

  const phaseRef = useRef<CoffeePhase>("idle");
  const pourRef = useRef(false);
  const fillRef = useRef(0);
  const cupsRef = useRef(0);
  const rafRef = useRef(0);
  const lastRef = useRef(0);
  const runRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  const mountedRef = useRef(true);

  const setPhaseBoth = useCallback((next: CoffeePhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const stopRaf = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
  }, []);

  const invalidate = useCallback(() => {
    runRef.current += 1;
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    stopRaf();
  }, [stopRaf]);

  const later = useCallback((ms: number, fn: () => void) => {
    const run = runRef.current;
    const id = window.setTimeout(() => {
      if (!mountedRef.current || run !== runRef.current) return;
      fn();
    }, ms);
    timersRef.current.push(id);
  }, []);

  const restPhase = useCallback((): CoffeePhase => {
    return cupsRef.current > 0 ? "ready" : "idle";
  }, []);

  const stopPour = useCallback(() => {
    if (!pourRef.current) return;
    pourRef.current = false;
    setIsPourActive(false);
    stopRaf();
    lastRef.current = 0;
    if (BUSY.includes(phaseRef.current)) return;
    setPhaseBoth(restPhase());
  }, [restPhase, setPhaseBoth, stopRaf]);

  const completeSip = useCallback(() => {
    const nextCups = cupsRef.current + 1;
    cupsRef.current = nextCups;
    const energy = energyForCups(nextCups);
    fillRef.current = 0;
    setCupFill(0);
    setCupsConsumed(nextCups);
    setDesignerEnergy(energy);
    setPhaseBoth("energized");
    setAnnouncement(
      `Cup finished. Designer energy ${energy}%. ${reactionForCups(nextCups)}`,
    );
    later(prefersReducedMotion() ? 0 : ENERGIZED_MS, () => {
      if (phaseRef.current === "energized") setPhaseBoth("ready");
    });
  }, [later, setPhaseBoth]);

  const drainMug = useCallback(
    (duration: number) => {
      const run = runRef.current;
      const start = performance.now();
      const tick = (now: number) => {
        if (!mountedRef.current || run !== runRef.current) return;
        const t = Math.min(1, (now - start) / duration);
        const next = 1 - t;
        fillRef.current = next;
        setCupFill(next);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          rafRef.current = 0;
          completeSip();
        }
      };
      stopRaf();
      rafRef.current = requestAnimationFrame(tick);
    },
    [completeSip, stopRaf],
  );

  const finishCup = useCallback(() => {
    if (BUSY.includes(phaseRef.current)) return;
    pourRef.current = false;
    setIsPourActive(false);
    stopRaf();
    lastRef.current = 0;
    fillRef.current = 1;
    setCupFill(1);
    setPhaseBoth("settling");
    const reduced = prefersReducedMotion();
    later(reduced ? 0 : SETTLE_MS, () => {
      setPhaseBoth("sipping");
      if (reduced) {
        later(180, completeSip);
      } else {
        drainMug(SIP_MS);
      }
    });
  }, [completeSip, drainMug, later, setPhaseBoth, stopRaf]);

  const frame = useCallback(
    (now: number) => {
      if (!mountedRef.current || !pourRef.current) return;
      const dt = now - lastRef.current;
      lastRef.current = now;
      const next = advanceFill(fillRef.current, dt);
      fillRef.current = next;
      setCupFill(next);
      if (next >= 1) {
        finishCup();
        return;
      }
      rafRef.current = requestAnimationFrame(frame);
    },
    [finishCup],
  );

  const startPour = useCallback(() => {
    if (BUSY.includes(phaseRef.current)) return;
    if (pourRef.current || fillRef.current >= 1) return;
    pourRef.current = true;
    setIsPourActive(true);
    setPhaseBoth("pouring");
    lastRef.current = performance.now();
    stopRaf();
    rafRef.current = requestAnimationFrame(frame);
  }, [frame, setPhaseBoth, stopRaf]);

  const pourMeasure = useCallback(() => {
    if (BUSY.includes(phaseRef.current) || pourRef.current) return;
    const next = addMeasure(fillRef.current);
    fillRef.current = next;
    setCupFill(next);
    if (next >= 1) {
      finishCup();
      return;
    }
    setPhaseBoth(restPhase());
  }, [finishCup, restPhase, setPhaseBoth]);

  const reset = useCallback(() => {
    invalidate();
    pourRef.current = false;
    fillRef.current = 0;
    cupsRef.current = 0;
    lastRef.current = 0;
    setIsPourActive(false);
    setCupFill(0);
    setDesignerEnergy(5);
    setCupsConsumed(0);
    setPhaseBoth("idle");
    setAnnouncement("Reset. Designer energy 5%. Running on empty.");
  }, [invalidate, setPhaseBoth]);

  const stopPourRef = useRef(stopPour);
  stopPourRef.current = stopPour;

  useEffect(() => {
    mountedRef.current = true;
    const onVisibility = () => {
      if (document.hidden) stopPourRef.current();
    };
    const onBlur = () => stopPourRef.current();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    return () => {
      mountedRef.current = false;
      pourRef.current = false;
      invalidate();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
    };
  }, [invalidate]);

  const busy = BUSY.includes(phase);
  const paused = !isPourActive && cupFill > 0 && cupFill < 1 && !busy;

  return {
    phase,
    isPourActive,
    cupFill,
    designerEnergy,
    cupsConsumed,
    announcement,
    busy,
    paused,
    startPour,
    stopPour,
    pourMeasure,
    reset,
    measure: MEASURE,
  };
}
